import { memoLast, sharedQuery } from './shared-query';
import { GS, gtinVariants, parseGs1, parseQty, parseScaleLabel, stripAim, type Product, type ScaleLayout } from '@sense/shared';
import { useSession } from './session';
import { db } from './db';

export interface Indexed { p: Product; hay: string; words: string[] }

const productsQ = sharedQuery(() => db.products.toArray());
const stockQ = sharedQuery(() => db.stock.toArray());
const EMPTY: Array<{ pid: number; qty: number }> = [];
const toStockMap = memoLast((rows: Array<{ pid: number; qty: number }>) => new Map(rows.map((r) => [r.pid, r.qty])));
const indexOf = memoLast((products: Product[]) => buildIndex(products));

/** All products, shared live by every screen (instant on revisits). */
export function useProducts(): Product[] | undefined {
  return productsQ.use();
}

export function useStockMap(): Map<number, number> {
  return toStockMap(stockQ.use() ?? EMPTY);
}

/** Search index over the active products, built once per catalogue change for the whole app. */
export function useProductIndex(): Indexed[] {
  const products = productsQ.use();
  return products ? indexOf(products) : NO_INDEX;
}
const NO_INDEX: Indexed[] = [];

/** Start loading the catalogue as soon as the app opens, so the first screen already has it. */
export function warmCatalog() { productsQ.start(); stockQ.start(); }

export function buildIndex(products: Product[]): Indexed[] {
  return products
    .filter((p) => p.active)
    .map((p) => {
      const hay = `${p.name} ${p.sku} ${p.category} ${p.name_local ?? ''}`.toLowerCase();
      return { p, hay, words: hay.split(/[^a-z0-9]+/).filter(Boolean) };
    });
}

export interface Lot { batch?: string; expiry?: number; serial?: string }
export interface Match { product: Product; qty?: number; price?: number; exact: boolean; lot?: Lot }

/** Some scanners can't send the GS1 separator; a store can pick a stand-in character (Settings → Scanner). */
export const gsStandIn = () => localStorage.getItem('sd.gsChar') ?? '';
const scaleLayout = (): ScaleLayout | null => (useSession.getState().me?.store.settings as { scale?: ScaleLayout } | undefined)?.scale ?? null;

function byBarcode(index: Indexed[], code: string): Indexed | undefined {
  const variants = gtinVariants(code);
  return index.find((i) => i.p.barcodes.some((b) => variants.includes(b)));
}

/**
 * Billing search. Supports:
 *   barcode / SKU exact      → exact match
 *   weighing-scale EAN-13    → product by PLU with weight or price
 *   "3*milk" or "1.25*onion" → quantity multiplier
 *   words                    → every word must prefix-match (name, sku, category)
 */
export function search(index: Indexed[], raw: string, limit = 40): { qty?: number; matches: Match[] } {
  let text = stripAim(raw.trim());
  const stand = gsStandIn();
  if (stand) text = text.split(stand).join(GS);
  let qty: number | undefined;
  // GS1-128 / DataBar / DataMatrix: GTIN plus batch, expiry, weight or count.
  if (text.includes(GS) || /^\(01\)/.test(text) || /^01\d{14}(1[0-7]|21|3\d)/.test(text)) {
    const g = parseGs1(text);
    const hit = g?.gtin ? byBarcode(index, g.gtin) : undefined;
    if (g && hit) {
      const q = g.weightGrams ?? (g.count ? g.count * 1000 : undefined) ?? (g.pricePaise && hit.p.price ? Math.round((g.pricePaise * 1000) / hit.p.price) : undefined);
      return { matches: [{ product: hit.p, qty: q, exact: true, lot: { batch: g.batch, expiry: g.expiry ?? g.bestBefore, serial: g.serial } }] };
    }
  }
  text = text.split(GS).join('');
  const mult = /^(\d+(?:[.,]\d+)?)\s*\*\s*(.*)$/.exec(text);
  if (mult) {
    qty = parseQty(mult[1]);
    text = mult[2].trim();
  }
  if (!text) return { qty, matches: [] };

  if (/^\d{4,14}$/.test(text)) {
    const scale = parseScaleLabel(text, scaleLayout());
    if (scale) {
      const hit = index.find((i) => i.p.plu === scale.plu);
      if (hit) {
        const q = scale.weightGrams != null ? scale.weightGrams : hit.p.price > 0 ? Math.round(((scale.pricePaise ?? 0) * 1000) / hit.p.price) : 1000;
        return { matches: [{ product: hit.p, qty: q, exact: true }] };
      }
    }
    const byCode = index.find((i) => i.p.barcodes.includes(text)) ?? byBarcode(index, text);
    if (byCode) return { qty, matches: [{ product: byCode.p, exact: true }] };
  }
  const lower = text.toLowerCase();
  const bySku = index.find((i) => i.p.sku.toLowerCase() === lower);
  if (bySku) return { qty, matches: [{ product: bySku.p, exact: true }] };

  const terms = lower.split(/\s+/).filter(Boolean);
  const scored: Array<{ m: Match; score: number }> = [];
  for (const i of index) {
    let ok = true;
    let score = 0;
    for (const t of terms) {
      const w = i.words.findIndex((word) => word.startsWith(t));
      if (w === -1) {
        if (!i.hay.includes(t)) { ok = false; break; }
        score += 3;
      } else score += w;
    }
    if (ok) scored.push({ m: { product: i.p, exact: false }, score: score + (i.hay.startsWith(lower) ? -5 : 0) });
  }
  scored.sort((a, b) => a.score - b.score || a.m.product.name.localeCompare(b.m.product.name));
  return { qty, matches: scored.slice(0, limit).map((s) => s.m) };
}

export const GST_RATES = [0, 500, 1200, 1800, 2800, 4000];
export const UNITS = ['pc', 'kg', 'g', 'l', 'ml', 'pack'] as const;
